"use client";

import { motion, useReducedMotion } from "framer-motion";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import { FloatingHeader } from "@/components/learn-piece/shell/floating-header";
import type { WidgetFront } from "./roll-widget-front";
import { FRONT_SLOT, useRollWidgetSlot, type RollWidgetAnchor } from "./roll-widget-slots";

/**
 * La largeur d'un panneau mis en avant, au plus.
 *
 * Un panneau se lit, et une ligne de texte trop longue se lit mal : l'agrandir
 * jusqu'aux marges du roll rendrait le premier plan moins lisible que le coin
 * d'où il vient. Une vignette vidéo, elle, prend toute la boîte — c'est le
 * widget qui décide, pas le premier plan.
 */
const FRONT_MAX_WIDTH_PX = 720;

type RollWidgetProps = {
  /** Le coin du roll où le widget se pose. */
  readonly anchor: RollWidgetAnchor;
  /**
   * Rang dans la pile de l'ancre, du bord vers l'intérieur. Sans lui, c'est
   * l'ordre de montage qui décide.
   */
  readonly order?: number;
  /**
   * Le widget est mis en avant : il quitte son coin pour le centre, agrandi,
   * par-dessus les autres. Décidé par le widget lui-même
   * ({@link useWidgetFront}), qui seul sait quel bouton l'y met.
   *
   * Son ancre ne change pas pendant ce temps : c'est là qu'il retourne.
   */
  readonly front?: boolean;
  readonly children: ReactNode;
};

/**
 * Pose un élément sur le roll, depuis n'importe quel composant du studio.
 *
 * ```tsx
 * <RollWidget anchor="bottom-right">
 *   <VideoWidget />
 * </RollWidget>
 * ```
 *
 * Un portail vers l'ancre du calque : le widget garde les contextes de
 * l'endroit où il est écrit (séance live, parcours, studio) sans que le calque
 * ait à les connaître. Tant que le calque n'est pas monté, il ne rend rien.
 */
export function RollWidget({ anchor, order, front = false, children }: RollWidgetProps) {
  const slot = useRollWidgetSlot(front ? FRONT_SLOT : anchor);

  if (slot === null) {
    return null;
  }

  return createPortal(
    // En avant, le widget se borne aussi en hauteur — `minHeight: 0` pour que
    // le plafond morde vraiment sur un élément de flex. Dans un coin, non : il
    // y déborde depuis toujours plutôt que de se faire écraser, et ce n'est pas
    // ce chantier-ci qui doit en décider autrement.
    <div
      style={
        front
          ? { maxWidth: "100%", maxHeight: "100%", minHeight: 0, pointerEvents: "auto" }
          : { order, maxWidth: "100%", pointerEvents: "auto" }
      }
    >
      {children}
    </div>,
    slot,
  );
}

type RollWidgetFrameProps = {
  /** Libellé en capitales de l'en-tête, comme les panneaux du parcours. */
  readonly kicker: string;
  readonly width: number;
  /** Fait tenu à droite de l'en-tête (un état, un compteur). */
  readonly trailing?: ReactNode;
  readonly footer?: ReactNode;
  /**
   * De quoi mettre ce panneau en avant, tel que {@link useWidgetFront} le rend.
   * Absent, le panneau n'en propose pas — tout n'a pas à s'agrandir.
   *
   * Le même objet se passe à `RollWidget` (`front={front.front}`) : l'un porte
   * le panneau au centre, l'autre y met le bouton.
   */
  readonly front?: WidgetFront;
  readonly onClose?: () => void;
  readonly ariaLabel?: string;
  readonly children: ReactNode;
};

/**
 * L'habit commun des widgets : le verre, l'en-tête et le pied des panneaux du
 * parcours (`FloatingShell` + `FloatingHeader`), pour qu'un widget se lise
 * comme un voisin de ces panneaux et non comme une fenêtre d'une autre app.
 *
 * Pas `FloatingShell` lui-même : il se positionne seul, publie la place qu'il
 * prend et partage un `layoutId` avec le parcours — trois choses qu'un widget
 * posé par le calque ne doit surtout pas faire.
 */
export function RollWidgetFrame({
  kicker,
  width,
  trailing,
  footer,
  front,
  onClose,
  ariaLabel,
  children,
}: RollWidgetFrameProps) {
  const reducedMotion = useReducedMotion();
  const ahead = front?.front === true;

  return (
    <motion.section
      aria-label={ariaLabel ?? kicker}
      initial={reducedMotion ? false : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={reducedMotion ? { duration: 0 } : { duration: 0.2, ease: "easeOut" }}
      style={{
        // En avant, jamais plus étroit qu'il ne l'était dans son coin, jamais
        // plus large que la boîte ni que ce qui se lit. `cqw` : c'est le
        // premier plan qui est le conteneur de taille (`roll-widget-layer`).
        width: ahead ? `max(${width}px, min(100cqw, ${FRONT_MAX_WIDTH_PX}px))` : width,
        maxWidth: "100%",
        maxHeight: ahead ? "100cqh" : undefined,
        display: "flex",
        flexDirection: "column",
        background: "var(--color-glass-shell)",
        backdropFilter: "blur(var(--blur-shell)) saturate(140%)",
        WebkitBackdropFilter: "blur(var(--blur-shell)) saturate(140%)",
        borderRadius: "var(--radius-shell)",
        boxShadow: "var(--shadow-popin-focus)",
        border: "1px solid var(--color-border-subtle)",
        overflow: "hidden",
      }}
    >
      <FloatingHeader
        kicker={kicker}
        trailing={
          front === undefined ? (
            trailing
          ) : (
            <>
              {trailing}
              <FrontButton front={front} />
            </>
          )
        }
        onClose={onClose}
      />

      {children}

      {footer !== undefined && (
        <footer
          className="flex items-center gap-2 px-4 py-2.5"
          style={{ borderTop: "1px solid var(--color-border-subtle)" }}
        >
          {footer}
        </footer>
      )}
    </motion.section>
  );
}

/**
 * Le bouton qui met un panneau en avant, et l'en sort. Dans l'en-tête, à
 * gauche de la croix : le geste voisine avec celui qui ferme, et on y revient
 * sans changer d'endroit.
 */
function FrontButton({ front }: { front: WidgetFront }) {
  return (
    <button
      type="button"
      onClick={front.front ? front.dismiss : front.promote}
      aria-label={front.front ? "Remettre dans le coin" : "Mettre en avant"}
      title={front.front ? "Remettre dans le coin" : "Mettre en avant"}
      className="grid h-6 w-6 place-items-center rounded-md text-text-muted transition-colors hover:bg-white/[0.06] hover:text-text-primary focus:outline-none focus-visible:ring-2"
      style={{ ["--tw-ring-color" as string]: "var(--color-accent)" }}
    >
      <FrontIcon ahead={front.front} />
    </button>
  );
}

/**
 * Quatre équerres, tournées vers l'extérieur pour agrandir et vers l'intérieur
 * pour revenir. Le même dessin partout où la mise en avant se propose — sur
 * l'en-tête d'un panneau comme sur l'image d'une vignette.
 */
export function FrontIcon({ ahead }: { ahead: boolean }) {
  return (
    <svg aria-hidden width="12" height="12" viewBox="0 0 12 12" fill="none">
      <g stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        {ahead ? (
          <>
            <path d="M4.5 1.5v3h-3" />
            <path d="M7.5 1.5v3h3" />
            <path d="M7.5 10.5v-3h3" />
            <path d="M4.5 10.5v-3h-3" />
          </>
        ) : (
          <>
            <path d="M1.5 4.5v-3h3" />
            <path d="M10.5 4.5v-3h-3" />
            <path d="M10.5 7.5v3h-3" />
            <path d="M1.5 7.5v3h3" />
          </>
        )}
      </g>
    </svg>
  );
}
